import { Action, Reducer } from 'redux';
import { ACTION, RequestCodetable, ReceiveCodetable, ValueChange, FailCodetable, InsertComponentDataToStore } from './action-types';
import { deepEquals } from 'app/infrastructure/js';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { SimpleFormState } from './types';

export const INITIAL_SIMPLEFORM_STATE: any = {
  simpleForm: {
    tempdata: {},
    editing: {
      values: null,
      persistedValues: null,
      hasChanges: false
    },
    componentData: {
    }
  }
};

const reducers: { [indexer: string]: Reducer<SimpleFormState, Action> } = {

  /**
   * Run when a component requests codetable options data
   */
  [ACTION.LOAD_OPTIONS_CODES]: (state: SimpleFormState, action: RequestCodetable): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          options: {
            ...state.simpleForm.tempdata.options,
            [action.context]: {
              ...state.simpleForm.tempdata.options?.[action.context],
              fetching: true
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component receives its code options data
   */
  [ACTION.LOAD_OPTIONS_CODES_OK]: (state: SimpleFormState, action: ReceiveCodetable): SimpleFormState => {
    const codes = action.data && action.data.map(codeItemToStoreValue);

    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          options: {
            ...state.simpleForm.tempdata.options,
            [action.context]: {
              data: codes,
              args: action.condition,
              value: action.value,
              fetching: false
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component fails to download codetable options data
   */
  [ACTION.LOAD_OPTIONS_CODES_FAIL]: (state: SimpleFormState, action: FailCodetable): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          options: {
            ...state.simpleForm.tempdata.options,
            [action.context]: {
              data: undefined,
              args: undefined,
              value: undefined,
              fetching: false,
              error: true
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component requests codetable categoryOptions data
   */
  [ACTION.LOAD_CATEGORY_CODES]: (state: SimpleFormState, action: RequestCodetable): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          categoryOptions: {
            ...state.simpleForm.tempdata.categoryOptions,
            [action.context]: {
              ...state.simpleForm.tempdata.categoryOptions?.[action.context],
              fetching: true
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component receives its code data
   */
  [ACTION.LOAD_CATEGORY_CODES_OK]: (state: SimpleFormState, action: ReceiveCodetable): SimpleFormState => {
    const codes = action.data && action.data.map(codeItemToStoreValue);

    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          categoryOptions: {
            ...state.simpleForm.tempdata.categoryOptions,
            [action.context]: {
              data: codes,
              args: action.condition,
              value: action.value,
              fetching: false
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component fails to download codetable data
   */
  [ACTION.LOAD_CATEGORY_CODES_FAIL]: (state: SimpleFormState, action: FailCodetable): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        tempdata: {
          ...state.simpleForm.tempdata,
          categoryOptions: {
            ...state.simpleForm.tempdata.categoryOptions,
            [action.context]: {
              data: undefined,
              args: undefined,
              value: undefined,
              fetching: false,
              error: true
            }
          }
        }
      }
    };
  },

  /**
   * Run when a component fires a onChange event
   */
  [ACTION.VALUE_CHANGE]: (state: SimpleFormState, action: ValueChange): SimpleFormState => {
    const value = action.value;
    const currentEditing = state.simpleForm.editing;
    const currentValues = {
      ...currentEditing.values,
      [action.context]: {
        value
      }
    };

    const modified = !deepEquals(currentValues, currentEditing.persistedValues);

    const newValues = { ...state.simpleForm.editing.values, [action.context]: { value } };

    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        editing: {
          ...state.simpleForm.editing,
          values: newValues,
          validation: {
            ...state.simpleForm.editing.validation,
            [action.context]: undefined
          },
          hasChanges: modified
        }
      }
    };
  },

  /**
   * Run when a component saves data to form-compatible format
   */
  [ACTION.INSERT_VALUE_TO_STORE]: (state: SimpleFormState, action: any): SimpleFormState => {

    const currentValidation = state.simpleForm.editing.validation || {};

    // filter outs validation for added fields
    const validation = Object.keys(currentValidation).reduce((memo, key) =>
      currentValidation[key] && action.output[key]
        ? memo
        : {
          ...memo,
          [key]: currentValidation[key]
        }
      , {});

    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        editing: {
          ...state.simpleForm.editing,
          values: { ...state.simpleForm.editing.values, ...action.output },
          persistedValues: { ...state.simpleForm.editing.persistedValues, ...action.output },
          validation
        }
      }
    };
  },

  /**
   * Run during initialization. Used to keep data about components in store.
   */
  [ACTION.INSERT_COMPONENT_DATA_TO_STORE]: (state: SimpleFormState, action: InsertComponentDataToStore): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        componentData: {
          ...state.simpleForm.componentData,
          [action.componentName]: {
            componentType: action.componentType,
            serialize: action.serialize,
            validationProps: action.validationProps
          }
        }
      }
    };
  },

  [ACTION.CLEAR_VALUE_IN_STORE]: (state: SimpleFormState, action: any): SimpleFormState => {
    if (!state.simpleForm || !state.simpleForm.editing || !state.simpleForm.editing.values) {
      return state;
    }

    const { [action.componentName]: _, ...values } = state.simpleForm.editing.values;
    const { [action.componentName]: __, ...componentData } = state.simpleForm.componentData;
    const { [action.componentName]: ___, ...persistedValues } = state.simpleForm.editing.persistedValues;

    const modified = !deepEquals(values, persistedValues);
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        editing: {
          ...state.simpleForm.editing,
          values,
          persistedValues,
          hasChanges: modified
        },
        componentData
      }
    };
  },

  [ACTION.CLEAR_ALL_VALUES]: (state: SimpleFormState, action: any): SimpleFormState => {
    return {
      ...state,
      ...INITIAL_SIMPLEFORM_STATE
    };
  },

  [ACTION.RESET_CHANGES_LISTENER]: (state: SimpleFormState, action: any): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        editing: {
          ...state.simpleForm.editing,
          hasChanges: false
        }
      }
    };
  },

  [ACTION.VALIDATION_CHANGE]: (state: SimpleFormState, action: any): SimpleFormState => {
    return {
      ...state,
      simpleForm: {
        ...state.simpleForm,
        editing: {
          ...state.simpleForm.editing,
          validation: action.validation
        }
      }
    };
  }

};

export const reducer: any = (path: string) => (state: SimpleFormState = INITIAL_SIMPLEFORM_STATE, action: any) => {
  const r = reducers[action.type];
  return (action.path === path && r && r(state, action)) || state;
};
